import { z } from "zod"

import type { ClientToolName } from "@/generated/course-material-api/types.generated"
import { parseJsonWithSchema } from "@/shared-module/common/utils/parseJsonWithSchema"

/**
 * The client tool this UI answers, matching `UpdateCheatingStatusTool::NAME` in
 * `services/headless-lms/chatbot/src/chatbot_tools/action_tools/update_cheating_status.rs`.
 */
export const UPDATE_CHEATING_STATUS_TOOL: ClientToolName = "update_cheating_status"

const decision = z.union([z.literal("confirm"), z.literal("dismiss")])

export type CheatingDecision = z.infer<typeof decision>

/** The arguments `UpdateCheatingStatusTool::parse_arguments` validates further server-side. */
const rawArguments = z.object({
  user_id: z.string(),
  course_id: z.string(),
  user_email: z.string(),
  course_name: z.string(),
  decision,
})

export interface UpdateCheatingStatusCall {
  toolCallId: string
  userId: string
  courseId: string
  userEmail: string
  courseName: string
  decision: CheatingDecision
}

/**
 * The call's raw arguments, parsed into what the bubble needs to render, or null if they cannot
 * be made sense of. This is the client tool registry's `parseCall` for this tool.
 */
export const parseUpdateCheatingStatusCall = (
  toolCallId: string,
  toolArguments: string,
): UpdateCheatingStatusCall | null => {
  const args = parseJsonWithSchema(toolArguments, rawArguments)
  if (!args) {
    return null
  }
  const userEmail = args.user_email.trim()
  const courseName = args.course_name.trim()
  if (userEmail.length === 0 || courseName.length === 0) {
    return null
  }
  return {
    toolCallId,
    userId: args.user_id,
    courseId: args.course_id,
    userEmail,
    courseName,
    decision: args.decision,
  }
}

const zUpdateCheatingStatusCall = z.object({
  toolCallId: z.string(),
  userId: z.string(),
  courseId: z.string(),
  userEmail: z.string(),
  courseName: z.string(),
  decision,
})

/**
 * Narrows a client tool registry entry's `unknown` call back to this tool's own type. Always true
 * for a call this tool's own `parseUpdateCheatingStatusCall` produced.
 */
export const isUpdateCheatingStatusCall = (call: unknown): call is UpdateCheatingStatusCall =>
  zUpdateCheatingStatusCall.safeParse(call).success
